import {
  INITIAL_CURSOR,
  SYNC_TABLES,
  ZERO_UUID,
  type OutboxOp,
  type PushResult,
  type PushedOp,
  type SyncCursor,
  type SyncStore,
  type SyncTable,
  type SyncTransport,
  SyncError,
} from './types.ts';

export interface SyncStatus {
  syncing: boolean;
  pending: number;
  rejected: number;
  lastSyncAt: number | null;
  /** Último error de red/servidor (se limpia al sincronizar bien). */
  lastError: string | null;
  /** Cuánto tardó la última sincronización completa (ms). */
  lastDurationMs: number | null;
}

export interface SyncEngineOptions {
  store: SyncStore;
  transport: SyncTransport;
  /** Negocio activo en el dispositivo (null = nada que sincronizar). */
  businessId: () => string | null;
  tables?: readonly SyncTable[];
  pageSize?: number;
  pushBatchSize?: number;
  now?: () => number;
  /**
   * Tiempo máximo de cada pedido al servidor. En celulares, un pedido puede quedar colgado para
   * siempre (la app pasó a segundo plano, cambió la red): sin tope, la sincronización quedaba
   * "Sincronizando" hasta reiniciar la app. Al vencer, se reintenta como un error de red.
   */
  requestTimeoutMs?: number;
}

const MAX_BACKOFF_MS = 5 * 60_000;

/** Corta la espera de un pedido que no responde (el pedido sigue, pero ya no frena la sincronización). */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SyncError('El servidor tardó demasiado en responder.', true)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export function backoffMs(attempts: number): number {
  return Math.min(MAX_BACKOFF_MS, 2_000 * 2 ** Math.max(0, attempts - 1));
}

export function compareCursor(a: SyncCursor, b: SyncCursor): number {
  const ax = BigInt(a.xid);
  const bx = BigInt(b.xid);
  if (ax !== bx) return ax > bx ? 1 : -1;
  return a.id === b.id ? 0 : a.id > b.id ? 1 : -1;
}

export class SyncEngine {
  private readonly opts: Required<Omit<SyncEngineOptions, 'store' | 'transport' | 'businessId'>> &
    Pick<SyncEngineOptions, 'store' | 'transport' | 'businessId'>;
  private running: Promise<void> | null = null;
  private rerun = false;
  private listeners = new Set<(s: SyncStatus) => void>();
  private status: SyncStatus = { syncing: false, pending: 0, rejected: 0, lastSyncAt: null, lastError: null, lastDurationMs: null };

  constructor(options: SyncEngineOptions) {
    this.opts = {
      tables: SYNC_TABLES,
      pageSize: 500,
      pushBatchSize: 200,
      now: () => Date.now(),
      requestTimeoutMs: 45_000,
      ...options,
    };
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  subscribe(listener: (s: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Pide una sincronización. Si ya hay una en curso, se encadena otra al terminar. */
  sync(): Promise<void> {
    if (this.running) {
      this.rerun = true;
      return this.running;
    }
    this.running = this.loop().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  /** Recalcula los contadores (p. ej. después de una escritura local). */
  async refreshCounts(): Promise<void> {
    const counts = await this.opts.store.countOps();
    this.update(counts);
  }

  private async loop(): Promise<void> {
    do {
      this.rerun = false;
      await this.runOnce();
    } while (this.rerun);
  }

  private async runOnce(): Promise<void> {
    const businessId = this.opts.businessId();
    const startedAt = this.opts.now();
    this.update({ syncing: true });
    try {
      if (businessId) {
        const userId = await this.request(this.opts.transport.currentUserId());
        if (userId) {
          await this.push(userId);
          await this.pull(businessId);
          this.update({ lastSyncAt: this.opts.now(), lastError: null, lastDurationMs: this.opts.now() - startedAt });
        }
      }
    } catch (error) {
      this.update({ lastError: error instanceof Error ? error.message : String(error) });
    } finally {
      const counts = await this.opts.store.countOps();
      this.update({ syncing: false, ...counts });
    }
  }

  private async push(userId: string): Promise<void> {
    const { store, transport } = this.opts;
    // Filas con cambios rechazados (o con un conflicto que ganó el servidor): al final se trae
    // la versión del servidor para no dejar en pantalla un valor que no quedó.
    const toRefetch = new Map<SyncTable, Set<string>>();
    const refetch = (op: OutboxOp) => {
      const ids = toRefetch.get(op.table) ?? new Set<string>();
      ids.add(op.row_id);
      toRefetch.set(op.table, ids);
    };
    const rejected = async (op: OutboxOp, error: string) => {
      await store.markRejected(op.seq!, error);
      if (op.kind === 'patch') refetch(op);
    };

    try {
      for (;;) {
        const ops = await store.nextPendingOps(userId, this.opts.pushBatchSize, this.opts.now());
        if (ops.length === 0) return;
        let results: PushResult[];
        try {
          results = await this.request(transport.pushBatch(ops));
        } catch (error) {
          // Falló el lote entero (red, servidor, función no disponible): se reintenta más
          // tarde, en el mismo orden. Nunca se descarta un lote completo por un error general.
          const message = error instanceof Error ? error.message : String(error);
          await store.markRetry(ops[0]!.seq!, message, this.opts.now() + backoffMs(ops[0]!.attempts + 1));
          throw error;
        }
        const pushed: PushedOp[] = [];
        for (const [i, op] of ops.entries()) {
          const result = results[i];
          if (result?.status === 'ok') {
            pushed.push({ op, xid: result.xid });
            // Conflicto: quedó el valor del servidor (más reciente) en algún campo.
            if (result.refetch) refetch(op);
          }
          else await rejected(op, result?.error ?? 'El servidor no informó el resultado de esta operación.');
        }
        await store.markPushed(pushed);
      }
    } finally {
      for (const [table, ids] of toRefetch) {
        const rows = await this.request(transport.fetchRows(table, [...ids])).catch(() => null);
        if (rows) await store.applyRows(table, rows);
      }
    }
  }

  private async pull(businessId: string): Promise<void> {
    const { store, transport, pageSize } = this.opts;
    for (const table of this.opts.tables) {
      let cursor: SyncCursor = (await store.getCursor(businessId, table)) ?? INITIAL_CURSOR;
      for (;;) {
        const { rows, safeXid } = await this.request(transport.pull(businessId, table, cursor, pageSize));
        const complete = rows.length < pageSize;
        const last = rows[rows.length - 1];
        // Página completa: seguir desde la última fila. Incompleta: ya estamos al día hasta safeXid.
        const next = complete || !last ? { xid: safeXid, id: ZERO_UUID } : { xid: String(last.server_xid), id: last.id };
        // El cursor nunca retrocede.
        if (compareCursor(next, cursor) > 0) cursor = next;
        await store.applyPulled(businessId, table, rows, cursor);
        if (complete) break;
      }
    }
  }

  private request<T>(promise: Promise<T>): Promise<T> {
    return withTimeout(promise, this.opts.requestTimeoutMs);
  }

  private update(patch: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...patch };
    for (const listener of this.listeners) listener(this.status);
  }
}
